/**
 * EnviarFormularioDialog — o formulario ENRIQUECER_LEAD sai daqui (FORM-12).
 *
 * Escolhe um formulario de enriquecimento ativo da unidade e gera o convite
 * pessoal (POST /api/crm/leads/{id}/convite-formulario — lead:edit). O token
 * chega SO nessa resposta (padrao C2): daqui o vendedor copia o link, abre o
 * WhatsApp com a mensagem pronta ou, com `formulario:responder`, preenche na
 * hora pelo painel (responder-interno, com o lead apontado pela sessao).
 *
 * O `?lead=<uuid>` que fazia esse papel na pagina publica deixou de existir.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiAlertCircle, FiCopy, FiEdit3, FiMessageCircle, FiSend } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  gerarConviteFormulario,
  listarFormulariosDeEnriquecimento,
  responderFormularioInterno,
  urlPublicaFormulario,
  visualizarFormularioPublico,
} from "../../../services/formularios";
import { linkWhatsapp } from "../../../utils/leadFormat";
import FormRenderer from "../../Formularios/FormRenderer";
import { Field, FormStack, InlineHint, Select } from "../styles";
import { WarningMsg } from "../../Orcamentos/styles";

function mensagemDoWhatsapp(url) {
  return `Olá! Para agilizar seu atendimento, complete seu cadastro por este link: ${url}`;
}

function EnviarFormularioDialog({
  open,
  onClose,
  leadId,
  telefone,
  podePreencher = false,
  onRespondido,
}) {
  const { request } = useAuth();
  const toast = useToast();
  const [formularios, setFormularios] = useState([]);
  const [leadEncerrado, setLeadEncerrado] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [formularioId, setFormularioId] = useState("");
  const [convite, setConvite] = useState(null);
  const [esquema, setEsquema] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro("");
    try {
      const payload = await listarFormulariosDeEnriquecimento(request, leadId, {
        silentError: true,
      });
      const lista = payload?.formularios || [];
      setFormularios(lista);
      setLeadEncerrado(Boolean(payload?.lead_encerrado));
      setFormularioId((atual) => atual || lista[0]?.id || "");
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar os formulários.");
    } finally {
      setCarregando(false);
    }
  }, [request, leadId]);

  useEffect(() => {
    if (!open) return;
    setConvite(null);
    setEsquema(null);
    carregar();
  }, [open, carregar]);

  const url = convite ? urlPublicaFormulario(convite.formulario.slug, { caminho: convite.caminho }) : "";
  const whatsapp = linkWhatsapp(telefone);

  const gerar = async () => {
    if (!formularioId) throw new Error("Escolha o formulário.");
    const res = await gerarConviteFormulario(request, leadId, formularioId, { silentError: true });
    setConvite(res);
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado.");
    } catch (_) {
      toast.info(url);
    }
  };

  const abrirWhatsapp = () => {
    const texto = encodeURIComponent(mensagemDoWhatsapp(url));
    window.open(`${whatsapp}?text=${texto}`, "_blank", "noopener,noreferrer");
  };

  const preencherAgora = async () => {
    // O esquema vem da rota publica com o convite: quem preenche no painel
    // nao precisa de formulario:view para ler a definicao.
    const dados = await visualizarFormularioPublico(convite.formulario.slug, {
      convite: convite.token,
    });
    setEsquema(dados);
  };

  const enviarPeloPainel = async (dados) => {
    setEnviando(true);
    try {
      await responderFormularioInterno(
        request,
        convite.formulario.id,
        { dados, lead: leadId },
        { silentError: true },
      );
      toast.success("Formulário respondido.");
      onRespondido?.();
      onClose();
    } finally {
      setEnviando(false);
    }
  };

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Enviar formulário"
      subtitle="Link pessoal para o cliente completar o cadastro"
      width="min(95vw, 560px)"
      footer={
        <>
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Fechar
          </button>
          {!convite && (
            <AsyncButton
              onClick={gerar}
              silentSuccess
              silentError
              errorMessage="Não foi possível gerar o link."
              disabled={!formularioId || leadEncerrado}
            >
              <FiSend size={14} aria-hidden="true" /> Gerar link
            </AsyncButton>
          )}
        </>
      }
    >
      <FormStack>
        {erro && (
          <WarningMsg>
            <FiAlertCircle size={14} aria-hidden="true" /> {erro}
          </WarningMsg>
        )}
        {leadEncerrado && (
          <WarningMsg>
            <FiAlertCircle size={14} aria-hidden="true" /> Lead encerrado: o
            cliente não completa mais o cadastro.
          </WarningMsg>
        )}
        {!carregando && !erro && formularios.length === 0 && (
          <WarningMsg>
            <FiAlertCircle size={14} aria-hidden="true" /> Nenhum formulário
            &quot;Enriquecer lead&quot; ativo nesta unidade. Crie um em
            Comercial → Formulários.
          </WarningMsg>
        )}

        {!convite && formularios.length > 0 && (
          <Field>
            Formulário
            <Select
              value={formularioId}
              onChange={(e) => setFormularioId(e.target.value)}
              aria-label="Formulário de enriquecimento"
            >
              {formularios.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}
                </option>
              ))}
            </Select>
            <InlineHint>
              O link vale por 30 dias e aceita um único envio. Só campos vazios
              do lead são preenchidos; o que o cliente informar diferente vai
              para a timeline.
            </InlineHint>
          </Field>
        )}

        {convite && !esquema && (
          <>
            <Field>
              Link pessoal — {convite.formulario.nome}
              <input className="input" readOnly value={url} aria-label="Link do convite" />
              <InlineHint>Cada link é de um cliente só. Não reutilize.</InlineHint>
            </Field>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button type="button" className="btn btn--secondary" onClick={copiar}>
                <FiCopy size={14} aria-hidden="true" /> Copiar link
              </button>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={abrirWhatsapp}
                disabled={!whatsapp}
                title={whatsapp ? "Abrir conversa no WhatsApp" : "Lead sem telefone válido"}
              >
                <FiMessageCircle size={14} aria-hidden="true" /> Enviar pelo WhatsApp
              </button>
              {podePreencher && (
                <AsyncButton
                  variant="secondary"
                  onClick={preencherAgora}
                  silentSuccess
                  errorMessage="Não foi possível abrir o formulário."
                >
                  <FiEdit3 size={14} aria-hidden="true" /> Preencher agora
                </AsyncButton>
              )}
            </div>
          </>
        )}

        {esquema && (
          <FormRenderer
            campos={esquema.campos || []}
            config={esquema.config || {}}
            submitLabel="Salvar respostas"
            submitting={enviando}
            onSubmit={enviarPeloPainel}
          />
        )}
      </FormStack>
    </SlideOverPanel>
  );
}

export default EnviarFormularioDialog;
